import {Platform, Share, ShareContent, StyleSheet, View, ViewStyle} from "react-native";
import {useRouter} from "expo-router";
import ErrorPage from "@/lib/pages/ErrorPage";
import SpinnerPage from "@/lib/pages/SpinnerPage";
import {useMemo, useState} from "react";
import Page from "@/lib/pages/Page";
import {API_URL, BASE_URL} from "@/lib/constants";
import {SvgUri} from "react-native-svg";
import Center from "@/lib/components/Center";
import Header from "@/lib/components/Header";
import Nav from "@/lib/components/Nav";
import ActionButton from "@/lib/components/ActionButton";
import * as Clipboard from 'expo-clipboard';
import {useGroup} from "@/lib/providers/GroupProvider";
import PrimaryText from "@/lib/components/PrimaryText";
import {pluralize} from "@/lib/language";
import AvatarBox from "@/lib/components/AvatarBox";
import {Membership} from "@/lib/types";

export default function SharePage() {
    const [copied, setCopied] = useState(false);
    const router = useRouter();

    const {group, groupLoading, groupError} = useGroup();

    const members = useMemo(() => {
        if (!group) {
            return [];
        }
        return group.members.filter(m => (
            m.status === Membership.ADMINISTRATOR ||
            m.status === Membership.MODERATOR ||
            m.status === Membership.MEMBER
        ));
    }, [group]);

    if (groupError) {
        return <ErrorPage error={groupError}/>
    } else if (groupLoading || !group) {
        return <SpinnerPage/>
    }

    const qrcodeSource = API_URL + `/group/${group.group_id}/qrcode`;
    const selfURL = BASE_URL + `/s/${group.group_id}`;
    const metadataStyle: ViewStyle = {
        borderColor: group.color,
    };
    const shareData: ShareContent | undefined = (() => {
        if (Platform.OS === "android") {
            return {message: selfURL}
        } else if (Platform.OS === "ios") {
            return {url: selfURL}
        }
    })();

    return (
        <Page>
            <Center width={330} style={{alignItems: "center"}}>
                <SvgUri uri={qrcodeSource} width={330} height={330} style={{maxWidth: "100%"}}/>
                <View style={[styles.metadata, metadataStyle]}>
                    <Header title={group.name}/>
                    {group.public && <PrimaryText>Offentlig samfunn</PrimaryText>}
                    {!group.public && <PrimaryText>Privat samfunn</PrimaryText>}
                    <AvatarBox
                        avatars={members.map(m => m.avatar)}
                        numItems={4}
                        color={group.color}
                        border={false}
                    >
                        <PrimaryText>{pluralize(members.length, "medlem", "medlemmer")}</PrimaryText>
                    </AvatarBox>
                </View>
                <Nav centered={true}>
                    {(Platform.OS === "ios" || Platform.OS === "android") &&
                        <ActionButton
                            icon={"share"}
                            title={"Del samfunnet"}
                            onPress={async () => {
                                await Share.share(shareData!);
                                router.dismiss();
                            }}
                        />
                    }
                    {Platform.OS !== "ios" &&
                        <ActionButton
                            icon={"link"}
                            title={(copied ? "Kopiert!" : "Kopier lenke")}
                            disabled={copied}
                            onPress={async () => {
                                await Clipboard.setStringAsync(selfURL);
                                setCopied(true);
                                setTimeout(() => setCopied(false), 1000);
                            }}
                        />
                    }
                </Nav>
            </Center>
        </Page>
    )
}

export const styles = StyleSheet.create({
    metadata: {
        width: "100%",
        flexDirection: "column",
        gap: 8,
        borderWidth: 1,
        borderRadius: 8,
        padding: 12,
    },
})